import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Commands } from '@shared/constants/commands';
import { Confirmation } from '@shared/models/confirmation';
import { AlertService } from '@shared/services/alert.service';
import { ApiService } from '@shared/services/api.service';
import { StorageService } from '@shared/services/storage.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-confirmations-modal',
  templateUrl: './confirmations-modal.component.html',
  styleUrls: ['./confirmations-modal.component.scss']
})
export class ConfirmationsModalComponent {
  @Input() deviceData: any;
  @Input() modalData!: Confirmation;

  @Output() confirm$ = new EventEmitter<boolean>();

  loading = false;

  private subscription = new Subscription();

  constructor(
    private api: ApiService,
    private alertService: AlertService,
    private router: Router,
    private translate: TranslateService,
    protected dialogRef: NbDialogRef<ConfirmationsModalComponent>,
    private storageService: StorageService
  ) { }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  cancel(): void {
    this.confirm$.next(false);
    this.dialogRef.close();
  }

  confirm(): void {
    this.confirm$.next(true);
    this.modalData.functionName && this[this.modalData.functionName]();
  }

  stopPrinting() {
    this.loading = true;

    this.api.postStopPrinting(this.deviceData.id)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success('commands.stop-printing-success');
          this.dialogRef.close();
        },
        error: err => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  resumePrinting() {
    this.loading = true;

    this.api.postResumePrinting(this.deviceData.id)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success('commands.resume-printing-success');
          this.dialogRef.close();
        },
        error: err => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  pausePrinting() {
    this.loading = true;
    
    this.api.postPausePrinting(this.deviceData.id)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success('commands.pause-printing-success');
          this.dialogRef.close();
        },
        error: err => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  deleteDevice(): void {
    this.loading = true;
    
    this.api.deleteDevice(this.deviceData)
      .subscribe({
        next: () => {
          this.loading = false;

          this.router.navigate(['dashboard']).then(() => {
            this.alertService.success('alerts.device-delete-success', 5000);
          });
          
          this.dialogRef.close(true);
        },
        error: err => {
          this.loading = false;
          this.dialogRef.close(false);
        }
      });
  }

  deleteDeviceFile(): void {
    this.loading = true;
    
    this.api.deleteDeviceFile(this.deviceData.deviceId, this.deviceData.fileId)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success('commands.delete-file-success');
          this.dialogRef.close('deleteFile');
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  deleteUserFromTeam(): void {
    this.loading = true;
    
    this.api.deleteUserFromTeam(this.deviceData.teamId, this.deviceData.userId)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success("alerts.user-delete-from-team-success", 5000);
          this.dialogRef.close(true);
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  deleteTeam(): void {
    this.loading = true;
    
    this.api.deleteTeam(this.deviceData.teamId)
      .subscribe({
        next: () => {
          this.loading = false;
          
          this.router.navigate(['/teams']).then(() => {
            this.alertService.success("alerts.delete-team-success", 5000);
          });
          
          this.dialogRef.close();
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  deleteDeviceFromTeam(): void {
    this.loading = true;
    
    this.api.deleteDeviceFromTeam(this.deviceData.teamId, this.deviceData.deviceId)
      .subscribe({
        next: availableTeamDevice => {
          this.loading = false;
          this.alertService.success("team.delete-device-success", 5000);
          this.dialogRef.close(availableTeamDevice);
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  deleteReportedProblem(): void {
    this.loading = true;
    
    this.api.deleteReportedProblem(this.deviceData)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success("alerts.delete-reported-problem-success", 5000);
          this.dialogRef.close();
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  deleteMaterial(): void {
    this.loading = true;

    this.api.deleteMaterial(this.deviceData.materialId)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success("alerts.delete-material-success", 5000);
          this.dialogRef.close(true);
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  deleteDeviceType(): void {
    this.loading = true;
    
    this.api.deleteDeviceType(this.deviceData.deviceTypeId)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success("alerts.delete-device-type-success", 5000);
          this.dialogRef.close(true);
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  requestDeviceUpdate(): void {
    this.loading = true;

    this.api.postStartUpdatingDevice(this.deviceData.deviceId)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success('commands.firmware-update-success');
          this.dialogRef.close();
        },
        error: err => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  confirmMessageOfUnavailability() {
    this.storageService.setSessionStorage("messageOfUnavailabilityDelivered", "true");
    this.dialogRef.close();
  }

  notifyAboutNewPrivacyPolicy(): void {
    this.subscription.add(
      this.api.getNotifyAboutNewVersionPrivacyPolicy()
        .subscribe({
          next: () => {
            this.loading = false;
            this.dialogRef.close();
          },
          error: (err: { error: number }) => {
            this.loading = false;
            this.dialogRef.close();
          }
        })
    );
  }

  notifyAboutNewIncloud(): void {
    const data = {
      version: this.deviceData.version,
      features: this.deviceData.features
    };
    
    this.loading = true;

    this.api.postInformationAboutNewVersion(data)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success('alerts.operation-completed-successfully', 5000);
          this.dialogRef.close(true);
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  deleteUserAvatar() {
    this.api.deleteUserAvatar()
      .subscribe({
        next: () => {
          this.alertService.success("alerts.delete-avatar-success");
          this.dialogRef.close();
        },
        error: (err: { error: number }) => {
          this.dialogRef.close();
        }
      });
  }

  deleteDeviceFromShelf(): void {
    this.loading = true;

    this.api.deleteDeviceFromShelf(this.deviceData.deviceId, this.deviceData.shelfId)
    .subscribe({
      next: () => {
        this.loading = false;
        this.alertService.success('alerts.device-delete-success', 5000);
        this.dialogRef.close(true);
      },
      error: (err: { error: number }) => {
        this.loading = false;
        this.dialogRef.close();
      }
    });
  }

  deleteShelf(): void {
    this.loading = true;
    this.api.deleteShelf(this.deviceData.shelfId)
      .subscribe({
        next: () => {
          this.loading = false;
          
          this.router.navigate(['/shelf']).then(() => {
            this.alertService.success("alerts.delete-shelf-success", 5000);
          });
          
          this.dialogRef.close();
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  leaveTeam() {
    this.loading = true;

    this.api.deleteUserLeaveTeam(this.deviceData.teamId)
      .subscribe({
        next: () => {
          this.loading = false;
          
          this.router.navigate((['teams'])).then(() => {
            this.alertService.success("alerts.user-leave-team-success");
          });

          this.dialogRef.close();
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  leaveShelf(): void {
    this.loading = true;

    this.api.deleteUserLeaveShelf(this.deviceData.shelfId)
      .subscribe({
        next: () => {
          this.loading = false;

          this.router.navigate((["shelf"])).then(() => {
            this.alertService.success("alerts.user-leave-shelf-success");
          });
          
          this.dialogRef.close();
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }

  removeDeviceFromMultiStream() {
    this.loading = true;

    this.api.deleteDeviceFromMultiStream(this.deviceData.deviceId)
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success("alerts.delete-device-from-multi-stream-success");
          this.dialogRef.close(true);
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close(false);
        }
    });
  }

  deleteVoucher() {
    this.loading = true;

    this.api.deleteVoucher(this.deviceData.voucherId)
      .subscribe({
        next: res => {
          this.loading = false;
          this.alertService.success("alerts.delete-voucher-success");
          this.dialogRef.close(true);
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close(false);
        }
      });
  }

  resendVoucher() {
    this.api.postSendVoucher({ code: this.deviceData.code })
      .subscribe({
        next: () => {
          this.loading = false;
          this.alertService.success("alerts.email-send-success");
          this.dialogRef.close();
        },
        error: (err: { error: number }) => {
          this.loading = false;
          this.dialogRef.close();
        }
      });
  }
}
